import { useState, useEffect, useRef } from 'react';
import { VideoItem } from '../components/VideoCarousel/VideoCarouselModal';
import { searchVideos } from '../utils/video-utils';
import { useFotbollPlayTeamChannels } from './useFotbollPlayTeamChannels';

interface UsePushDeeplinkSearchReturn {
  searchQuery: string;
  setSearchQuery: (query: string) => void;
  searchResults: VideoItem[];
  isSearching: boolean;
}

export function usePushDeeplinkSearch(teamAbbrv: string): UsePushDeeplinkSearchReturn {
  const [searchQuery, setSearchQuery] = useState('');
  const [searchResults, setSearchResults] = useState<VideoItem[]>([]);
  const [isSearching, setIsSearching] = useState(false);
  const debounceRef = useRef<NodeJS.Timeout | null>(null);
  const { channels } = useFotbollPlayTeamChannels(teamAbbrv);

  useEffect(() => {
    if (!searchQuery.trim()) {
      setSearchResults([]);
      return;
    }

    if (channels.length === 0) {
      setSearchResults([]);
      return;
    }

    if (debounceRef.current) {
      clearTimeout(debounceRef.current);
    }

    debounceRef.current = setTimeout(async () => {
      setIsSearching(true);
      try {
        const results = await searchVideos(searchQuery, { channels });
        setSearchResults(results);
      } catch (error) {
        console.error('Failed to search for push/deeplink:', error);
        setSearchResults([]);
      } finally {
        setIsSearching(false);
      }
    }, 300);

    return () => {
      if (debounceRef.current) {
        clearTimeout(debounceRef.current);
      }
    };
  }, [channels, searchQuery]);

  return {
    searchQuery,
    setSearchQuery,
    searchResults,
    isSearching,
  };
}
